import React from "react";
import {
  Card,
  Row,
  Col,
  Tag,
  Typography,
  Image,
  Tooltip,
  Button,
  Space,
  message,
} from "antd";
import {
  InboxOutlined,
  CopyOutlined,
  ExportOutlined,
  CheckCircleOutlined,
  UserOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

const ShippingPackagesSection = ({ packages = [], isDarkMode = false }) => {
  const { t } = useTranslation();
  if (!packages || packages.length === 0) {
    return (
      <div
        style={{
          padding: "40px",
          textAlign: "center",
          color: "#8c8c8c",
          backgroundColor: isDarkMode ? "#1f1f1f" : "#fafafa",
          borderRadius: 12,
          border: "1px dashed #d9d9d9",
        }}
      >
        <InboxOutlined style={{ fontSize: 32, marginBottom: 8, color: "#bfbfbf" }} />
        <div>{t("orderDetails.noPackagesFound", "No shipping packages (boxes) recorded for this order yet.")}</div>
      </div>
    );
  }

  const getPackageStatusColor = (status) => {
    const s = String(status || "").toLowerCase();
    if (s.includes("track")) return "blue";
    if (s.includes("arriv") || s.includes("deliver")) return "green";
    if (s.includes("not grouped") || s.includes("loose")) return "default";
    return "cyan";
  };

  return (
    <Row gutter={[16, 16]}>
      {packages.map((pkg) => {
        const trackingNum = pkg.shipping_order?.tracking_number;
        const provider = pkg.shipping_order?.provider || "aramex";
        const trackingUrl =
          provider.toLowerCase() === "aramex" && trackingNum
            ? `https://www.aramex.com/track/results?mode=0&ShipmentNumber=${trackingNum}`
            : null;

        return (
          <Col xs={24} sm={12} lg={8} key={pkg.id}>
            <Card
              size="small"
              style={{
                borderRadius: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                border: "1px solid #e8e8e8",
                height: "100%",
                background: isDarkMode ? "#1f1f1f" : "#fff",
              }}
              title={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontWeight: 700, fontSize: 14 }}>
                    <InboxOutlined style={{ marginRight: 6, color: "#1677ff" }} />
                    {t("orderDetails.box", { id: pkg.id, defaultValue: `Box #${pkg.id}` })}
                  </span>
                  <Space size={4}>
                    {pkg.shipping_order?.provider && (
                      <Tag color="orange" style={{ textTransform: "capitalize", margin: 0 }}>
                        {pkg.shipping_order.provider}
                      </Tag>
                    )}
                    <Tag
                      color={getPackageStatusColor(pkg.status)}
                      style={{ margin: 0 }}
                    >
                      {pkg.status || t("orderDetails.loose", "Loose")}
                    </Tag>
                  </Space>
                </div>
              }
            >
              <div style={{ display: "flex", gap: 14, marginBottom: 12 }}>
                {/* Photo Thumbnail */}
                {pkg.photo_url ? (
                  <div
                    style={{
                      width: 90,
                      height: 90,
                      borderRadius: 8,
                      overflow: "hidden",
                      flexShrink: 0,
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <Image
                      src={pkg.photo_url}
                      alt={`Box #${pkg.id}`}
                      width={90}
                      height={90}
                      style={{ objectFit: "cover" }}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      width: 90,
                      height: 90,
                      borderRadius: 8,
                      backgroundColor: isDarkMode ? "#141414" : "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#bfbfbf",
                      flexShrink: 0,
                    }}
                  >
                    <InboxOutlined style={{ fontSize: 24 }} />
                  </div>
                )}

                {/* Dimensions & Weight */}
                <div style={{ flex: 1, fontSize: 13 }}>
                  <div style={{ marginBottom: 4 }}>
                    <Text type="secondary">{t("orderDetails.weight", "Weight:")} </Text>
                    <Text strong>{pkg.weight_kg ? `${pkg.weight_kg} kg` : "N/A"}</Text>
                  </div>

                  <div style={{ marginBottom: 6 }}>
                    <Text type="secondary">{t("orderDetails.dimensions", "Dimensions:")} </Text>
                    <Text strong>
                      {pkg.length_cm || 0} × {pkg.width_cm || 0} × {pkg.height_cm || 0} cm
                    </Text>
                  </div>

                  {pkg.confirmed_by && (
                    <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                      <UserOutlined style={{ marginRight: 4 }} />
                      {pkg.confirmed_by}
                    </div>
                  )}

                  {pkg.confirmed_at && (
                    <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                      <ClockCircleOutlined style={{ marginRight: 4 }} />
                      {new Date(pkg.confirmed_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>

              {/* Tracking row */}
              {trackingNum && (
                <div
                  style={{
                    background: isDarkMode ? "#141414" : "#f9f9f9",
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: "1px dashed #d9d9d9",
                    marginBottom: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 11, color: "#8c8c8c" }}>{t("orderDetails.awb", "AWB:")}</span>
                    <span
                      style={{
                        fontFamily: "monospace",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#1677ff",
                      }}
                    >
                      {trackingNum}
                    </span>
                  </div>

                  <Space size={4}>
                    <Tooltip title={t("orderDetails.copyTrackingNumber", "Copy Tracking Number")}>
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined style={{ color: "#1677ff" }} />}
                        onClick={() => {
                          navigator.clipboard.writeText(trackingNum);
                          message.success(t("orderDetails.trackingNumberCopied", "Tracking number copied!"));
                        }}
                      />
                    </Tooltip>
                    {trackingUrl && (
                      <Tooltip title={t("orderDetails.trackOnAramex", "Track on Aramex")}>
                        <Button
                          type="text"
                          size="small"
                          icon={<ExportOutlined style={{ color: "#fa8c16" }} />}
                          href={trackingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      </Tooltip>
                    )}
                  </Space>
                </div>
              )}

              {/* Items in this box */}
              {pkg.items && pkg.items.length > 0 && (
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#8c8c8c",
                      marginBottom: 4,
                      textTransform: "uppercase",
                    }}
                  >
                    {t("orderDetails.itemsInBox", { count: pkg.items.length, defaultValue: `Items in this box (${pkg.items.length}):` })}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {pkg.items.map((it, idx) => (
                      <Tag
                        key={idx}
                        style={{
                          fontSize: 11,
                          padding: "2px 6px",
                          borderRadius: 4,
                          margin: 0,
                        }}
                      >
                        {t("orderDetails.itemNumber", { id: it.order_item_id, defaultValue: `Item #${it.order_item_id}` })}{" "}
                        <span style={{ color: "#1677ff", fontWeight: 600 }}>
                          ×{it.quantity_in_package}
                        </span>
                        {it.label_changed_confirmed && (
                          <Tooltip title={t("orderDetails.labelChangedConfirmed", "Label changed confirmed")}>
                            <CheckCircleOutlined
                              style={{ color: "#52c41a", marginLeft: 4 }}
                            />
                          </Tooltip>
                        )}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </Col>
        );
      })}
    </Row>
  );
};

export default ShippingPackagesSection;
